import { CheckCircleOutlined } from '@ant-design/icons';
import { Descriptions, Table, Tag, Typography } from 'antd';
import { useEffect, useState } from 'react';

import { FacilityDto, FacilityInterestedNurseDto } from '@clh/api-client';
import {
    ContactPreferenceLabels,
    formatInterestChoices,
    InterestAvailabilityLabels,
    InterestShiftLengthLabels,
    InterestShiftTypeLabels,
    InterestWorkTypeLabels,
    TravelWillingnessLabels,
    WorkTypePreferenceLabels,
} from '@clh/util';

import { useApiClient } from '../../hooks/use-api-client';
import { useSelection } from '../../hooks/use-selection';

const { WEBAPP_BASE_URL } = ENV_CONFIG_VARS;

const workTypeText = (row: FacilityInterestedNurseDto) =>
    row.formVersion === 2
        ? (formatInterestChoices(
              row.workTypes,
              InterestWorkTypeLabels,
              row.workTypeOther
          ) ?? '')
        : row.workTypePreference
          ? (WorkTypePreferenceLabels[row.workTypePreference] ??
            row.workTypePreference)
          : '';

const startText = (row: FacilityInterestedNurseDto) =>
    row.formVersion === 2
        ? (formatInterestChoices(
              row.availability ? [row.availability] : [],
              InterestAvailabilityLabels,
              row.availabilityOther
          ) ?? '')
        : (row.startDateComment ?? '');

const FirstFormAnswers = ({ row }: { row: FacilityInterestedNurseDto }) => (
    <Descriptions size="small" column={1} bordered>
        <Descriptions.Item label="Specialties">
            {row.interestSpecialtyCodes?.map((s) => (
                <Tag key={s}>{s}</Tag>
            ))}
        </Descriptions.Item>
        <Descriptions.Item label="Travel">
            {row.willingToTravel &&
                (TravelWillingnessLabels[row.willingToTravel] ??
                    row.willingToTravel)}
        </Descriptions.Item>
        <Descriptions.Item label="Start Date">
            {row.startDateComment}
        </Descriptions.Item>
        <Descriptions.Item label="Comment">{row.comment}</Descriptions.Item>
    </Descriptions>
);

const SecondFormAnswers = ({ row }: { row: FacilityInterestedNurseDto }) => {
    const answers = [
        {
            label: 'Specialties',
            value: row.interestSpecialtyCodes.length
                ? row.interestSpecialtyCodes.map((s) => <Tag key={s}>{s}</Tag>)
                : null,
        },
        {
            label: 'Work Type',
            value: formatInterestChoices(
                row.workTypes,
                InterestWorkTypeLabels,
                row.workTypeOther
            ),
        },
        {
            label: 'Shift Types',
            value: formatInterestChoices(
                row.shiftTypes,
                InterestShiftTypeLabels,
                row.shiftTypeOther
            ),
        },
        {
            label: 'Shift Lengths',
            value: formatInterestChoices(
                row.shiftLengths,
                InterestShiftLengthLabels
            ),
        },
        {
            label: 'Availability',
            value: formatInterestChoices(
                row.availability ? [row.availability] : [],
                InterestAvailabilityLabels,
                row.availabilityOther
            ),
        },
        { label: 'Minimum Rate', value: row.minimumRate?.trim() },
        {
            label: 'Contact Preference',
            value:
                row.contactPreference &&
                ContactPreferenceLabels[row.contactPreference],
        },
        { label: 'Note', value: row.comment?.trim() },
    ];

    return (
        <Descriptions size="small" column={1} bordered>
            {answers
                .filter(({ value }) => value)
                .map(({ label, value }) => (
                    <Descriptions.Item key={label} label={label}>
                        {value}
                    </Descriptions.Item>
                ))}
        </Descriptions>
    );
};

export default function InterestsTab() {
    const api = useApiClient();
    const { selection } = useSelection<FacilityDto>();
    const [interests, setInterests] = useState<FacilityInterestedNurseDto[]>(
        []
    );
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        if (!api) {
            return;
        }

        setIsLoading(true);
        api.facilityControllerListInterestedNurses({
            facilityId: selection.id,
        })
            .then(setInterests)
            .finally(() => setIsLoading(false));
    }, [selection.id]);

    return (
        <div>
            <Typography.Title level={4}>Interested Nurses</Typography.Title>
            <Table
                rowKey="nurseProfileId"
                bordered
                size="small"
                dataSource={interests}
                loading={isLoading}
                expandable={{
                    expandedRowRender: (row) =>
                        row.formVersion === 2 ? (
                            <SecondFormAnswers row={row} />
                        ) : (
                            <FirstFormAnswers row={row} />
                        ),
                }}
                columns={[
                    {
                        title: 'Nurse',
                        render: (_, row) => (
                            <a
                                href={`${WEBAPP_BASE_URL}/site/nurse/${row.nurseProfileId}`}
                                target="_blank"
                                rel="noreferrer"
                            >
                                {row.firstName} {row.lastName}
                            </a>
                        ),
                        sorter: (a, b) =>
                            `${a.lastName}${a.firstName}`.localeCompare(
                                `${b.lastName}${b.firstName}`
                            ),
                    },
                    {
                        title: 'Form',
                        dataIndex: 'formVersion',
                        width: 70,
                        render: (version: number) => (
                            <Tag color={version === 2 ? 'blue' : undefined}>
                                v{version}
                            </Tag>
                        ),
                        filters: [
                            { text: 'v1', value: 1 },
                            { text: 'v2', value: 2 },
                        ],
                        onFilter: (value, row) => row.formVersion === value,
                    },
                    {
                        title: 'Work Type',
                        render: (_, row) => workTypeText(row),
                        sorter: (a, b) =>
                            workTypeText(a).localeCompare(workTypeText(b)),
                    },
                    {
                        title: 'Start',
                        render: (_, row) => startText(row),
                        sorter: (a, b) =>
                            startText(a).localeCompare(startText(b)),
                    },
                    {
                        title: 'Submitted',
                        dataIndex: 'createdAt',
                        render: (value: Date) =>
                            value ? new Date(value).toLocaleDateString() : '',
                        sorter: (a, b) =>
                            new Date(a.createdAt).getTime() -
                            new Date(b.createdAt).getTime(),
                        defaultSortOrder: 'descend',
                    },
                    {
                        title: 'Complete',
                        width: 80,
                        render: (_, row) =>
                            row.isProfileComplete ? (
                                <CheckCircleOutlined />
                            ) : null,
                    },
                ]}
            />
        </div>
    );
}
